// ForWhomSection.js - Section 5 : À qui s'adresse ETEF Design ?
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  MdRocketLaunch,
  MdCheckCircle,
  MdArrowForward,
  MdAutoAwesome,
  MdVerified,
  MdInsights,
  MdTimeline,
  MdPeople,
  MdBusiness,
  MdSchool,
  MdWork,
  MdGroups,
  MdStars,
  MdEmojiPeople,
  MdTrendingUp,
  MdFlashOn,
  MdDns
} from 'react-icons/md';
import { 
  FaChartLine, 
  FaCrown, 
  FaInfinity,
  FaRobot,
  FaStar,
  FaTrophy,
  FaBriefcase,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaUserTie,
  FaLaptopCode,
  FaSeedling,
  FaBuilding,
  FaUsers
} from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiCrystalBall, GiSpiralShell, GiTargetArrows, GiProgression } from 'react-icons/gi';
import './ForWhomSection.css';

const ForWhomSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isInView = useInView(sectionRef, { once: false, amount: 0.2 });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.95, 1, 1, 0.95]);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const targets = [
    { icon: <FaUserTie />, name: "Entrepreneurs", color: "#3B82F6", delay: 0 },
    { icon: <FaBuilding />, name: "PME", color: "#60A5FA", delay: 0.05 },
    { icon: <FaSeedling />, name: "Startups", color: "#8B5CF6", delay: 0.1 },
    { icon: <FaChalkboardTeacher />, name: "Organismes de formation", color: "#10B981", delay: 0.15 },
    { icon: <FaGraduationCap />, name: "Écoles", color: "#F59E0B", delay: 0.2 },
    { icon: <FaBriefcase />, name: "Cabinets de conseil", color: "#EC4899", delay: 0.25 },
    { icon: <FaUsers />, name: "Commerciaux", color: "#06B6D4", delay: 0.3 },
    { icon: <FaLaptopCode />, name: "Prestataires de services", color: "#3B82F6", delay: 0.35 }
  ];

  return (
    <section className="forwhom-root" ref={sectionRef}>
      {/* Background orbs animés */}
      <div className="forwhom-orbs">
        <div className="forwhom-orb forwhom-orb-s1" style={{ transform: `translate(${scrollProgress * 80}px, -${scrollProgress * 40}px)` }} />
        <div className="forwhom-orb forwhom-orb-s2" style={{ transform: `translate(-${scrollProgress * 60}px, ${scrollProgress * 50}px)` }} />
        <div className="forwhom-orb forwhom-orb-s3" style={{ transform: `translate(${scrollProgress * 50}px, ${scrollProgress * 30}px)` }} />
      </div>

      {/* Éléments flottants */}
      <div className="forwhom-floating">
        <GiAstronautHelmet className="forwhom-float-icon fw1" />
        <GiStarsStack className="forwhom-float-icon fw2" />
        <GiCrystalBall className="forwhom-float-icon fw3" />
        <FaRobot className="forwhom-float-icon fw4" />
        <GiGalaxy className="forwhom-float-icon fw5" />
      </div>

      <motion.div 
        className="forwhom-container"
        style={{ opacity, scale }}
      >
        {/* Header */}
        <motion.div 
          className="forwhom-header"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div 
            className="forwhom-badge-wrapper"
            whileHover={{ scale: 1.03 }}
          >
            <span className="forwhom-badge">
              <MdPeople className="forwhom-badge-icon" />
              <span>Pour qui ? — Notre audience</span>
              <MdEmojiPeople className="forwhom-badge-icon" />
            </span>
            <div className="forwhom-badge-glow"></div>
          </motion.div>

          <motion.h2 
            className="forwhom-title"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="forwhom-title-line">
              À qui s'adresse <span className="forwhom-title-gradient">ETEF Design</span>
            </span>
            <span className="forwhom-title-line">
              <span className="forwhom-title-highlight">?</span>
            </span>
            <div className="forwhom-title-underline"></div>
          </motion.h2>

          <motion.p 
            className="forwhom-subtitle"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Nos solutions sont pensées pour les entreprises qui veulent <span className="forwhom-highlight">gagner du temps</span>, 
            <br />mieux suivre leurs prospects et <span className="forwhom-highlight">obtenir plus de clients</span> sans travailler plus.
          </motion.p>
        </motion.div>

        {/* Grille des cibles */}
        <motion.div 
          className="forwhom-grid"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="forwhom-grid-header">
            <h3>
              <FaUsers className="forwhom-header-icon" />
              Nos solutions s'adressent à
            </h3>
            <div className="forwhom-header-line"></div>
          </div>

          <div className="forwhom-items-grid">
            {targets.map((target, index) => (
              <motion.div
                key={index}
                className="forwhom-card"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + target.delay }}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className="forwhom-card-glow" style={{ background: `radial-gradient(circle, ${target.color}20, transparent 80%)` }} />
                <div className="forwhom-icon-wrapper" style={{ borderColor: `${target.color}40`, background: `${target.color}10` }}>
                  <div className="forwhom-icon" style={{ color: target.color }}>
                    {target.icon}
                  </div>
                </div>
                <h4 className="forwhom-card-title">{target.name}</h4>
                <div className="forwhom-check">
                  <MdCheckCircle style={{ color: target.color }} />
                  <span>Solution adaptée</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Message de valeur */}
        <motion.div 
          className="forwhom-value-message"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="forwhom-value-card">
            <div className="forwhom-value-icon">
              <MdStars />
            </div>
            <p>
              Que vous soyez <span className="forwhom-gradient-text">indépendant, petite entreprise ou grande structure</span>, 
              nous adaptons nos solutions à votre activité pour vous offrir un système sur mesure.
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="forwhom-cta-wrapper"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 1 }}
        >
          <div className="forwhom-cta-card">
            <div className="forwhom-cta-glow"></div>
            <div className="forwhom-cta-pattern"></div>
            
            <div className="forwhom-cta-content">
              <motion.div 
                className="forwhom-cta-icon"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <MdAutoAwesome />
              </motion.div>
              <h3>Vous correspondez à <span className="forwhom-gradient-text">l'un de ces profils</span> ?</h3>
              <p>Découvrons ensemble comment nous pouvons vous aider à atteindre vos objectifs.</p>
              
              <div className="forwhom-cta-features">
                <div className="forwhom-cta-feature-item">
                  <MdFlashOn />
                  <span>Diagnostic gratuit</span>
                </div>
                <div className="forwhom-cta-feature-item">
                  <MdInsights />
                  <span>Solution sur mesure</span>
                </div>
                <div className="forwhom-cta-feature-item">
                  <MdVerified />
                  <span>Résultats garantis</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="forwhom-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Discuter de mon projet</span>
              <MdArrowForward />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ForWhomSection;